import { useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { MutationCache, QueryCache } from '@tanstack/react-query';
import { semConexao } from '../api/client.js';

/**
 * O servidor está fora de alcance?
 *
 * Sem pergunta extra e sem sondagem periódica: o app conversa com a API o tempo
 * todo, então o estado da conexão já está escrito no cache de consultas. Basta
 * comparar quando foi a última falha de rede com quando foi o último sucesso —
 * se a falha é mais recente, estamos fora.
 *
 * `navigator.onLine` não serve aqui. Ele diz que o aparelho tem rede, não que o
 * cluster de casa está alcançável: no Wi-Fi com a rota quebrada ele responde
 * `true` o tempo inteiro, que foi exatamente o caso do Tailscale sem
 * encaminhamento de IP.
 */
function servidorInalcancavel(consultas: QueryCache, gravacoes: MutationCache): boolean {
  let ultimaFalha = 0;
  let ultimoSucesso = 0;

  for (const query of consultas.getAll()) {
    const { error, errorUpdatedAt, dataUpdatedAt } = query.state;
    if (semConexao(error) && errorUpdatedAt > ultimaFalha) ultimaFalha = errorUpdatedAt;
    if (dataUpdatedAt > ultimoSucesso) ultimoSucesso = dataUpdatedAt;
  }

  // A gravação também conta. Numa tela parada, sem consulta nenhuma em
  // andamento, o único sinal de que a conexão caiu é o "Bebi esta" que falhou —
  // e a faixa precisa aparecer ali, dizendo que o problema não é só daquele botão.
  for (const mutation of gravacoes.getAll()) {
    const { error, submittedAt, status } = mutation.state;
    if (semConexao(error) && submittedAt > ultimaFalha) ultimaFalha = submittedAt;
    if (status === 'success' && submittedAt > ultimoSucesso) ultimoSucesso = submittedAt;
  }

  return ultimaFalha > ultimoSucesso;
}

export function useServidorInalcancavel(): boolean {
  const client = useQueryClient();
  const consultas = client.getQueryCache();
  const gravacoes = client.getMutationCache();

  return useSyncExternalStore(
    (aoMudar) => {
      const pararConsultas = consultas.subscribe(aoMudar);
      const pararGravacoes = gravacoes.subscribe(aoMudar);
      return () => {
        pararConsultas();
        pararGravacoes();
      };
    },
    () => servidorInalcancavel(consultas, gravacoes),
  );
}

/**
 * A faixa de "sem conexão".
 *
 * Existe porque o app escondia a queda: navegar continuava funcionando e o erro
 * só aparecia na hora de salvar — quando a pessoa já tinha decidido agir. O
 * aviso chega antes do toque, e diz as duas coisas que importam: o que está na
 * tela pode estar velho, e gravar não vai funcionar agora.
 *
 * Fica acima da barra de navegação, e não no topo, porque cada tela já tem seu
 * próprio cabeçalho fixo ali.
 */
export function AvisoDeConexao() {
  const inalcancavel = useServidorInalcancavel();
  const client = useQueryClient();

  if (!inalcancavel) return null;

  return (
    <div
      role="status"
      className="fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-40 px-4 pb-2"
    >
      <div className="mx-auto flex max-w-3xl items-center gap-3 rounded-xl border border-amber-900/60 bg-amber-950/90 px-4 py-3 text-sm text-amber-100 shadow-lg backdrop-blur">
        <span aria-hidden className="text-base">
          📡
        </span>
        <p className="flex-1 leading-snug">
          Sem conexão com o servidor. O que está na tela pode estar desatualizado, e nada que você
          salvar agora vai ser gravado.
        </p>
        <button
          type="button"
          className="shrink-0 rounded-lg border border-amber-700/70 px-3 py-1.5 text-xs font-semibold active:bg-amber-900/60"
          onClick={() => void client.refetchQueries()}
        >
          Tentar
        </button>
      </div>
    </div>
  );
}
